import { z } from 'zod';

export const NIVEIS = ['critico', 'atencao', 'regular'] as const;
// Aceita os 3 conhecidos; valores fora do enum caem em 'regular' via .catch.
// Mantém type literal Nivel = 'critico' | 'atencao' | 'regular' pro lookup.
export const nivelSchema = z.enum(NIVEIS).catch('regular');
export type Nivel = z.infer<typeof nivelSchema>;

export const STATUS_VERIF = [
  'regular',
  'pendente',
  'irregular',
  'alerta',
  'atencao',
] as const;
export const statusVerifSchema = z.enum(STATUS_VERIF);
export type StatusVerif = z.infer<typeof statusVerifSchema>;

// Item de lista — agregado por cliente
export const diagnosticoListItemSchema = z.object({
  cpf: z.string(),
  nome: z.string(),
  municipio: z.string().nullish(),
  score: z.coerce.number(),
  nivel: nivelSchema,
  pendencias: z.coerce.number().default(0),
  // Back-end retorna [{verificacao, oportunidade}] (objects), não strings.
  // Permissivo via z.unknown() — UI só lê .length.
  oportunidades: z.array(z.unknown()).default([]),
  ultima_verificacao: z.string().nullish(),
});
export type DiagnosticoListItem = z.infer<typeof diagnosticoListItemSchema>;

export const diagnosticoListSchema = z.object({
  ultima_varredura: z.string().nullish(),
  clientes: z.array(diagnosticoListItemSchema).default([]),
  total_oportunidades: z.coerce.number().default(0),
});
export type DiagnosticoList = z.infer<typeof diagnosticoListSchema>;

// Verificação individual (item da árvore de checks)
export const verificationSchema = z.object({
  verificacao: z.string(),
  status: z.string(),
  detalhe: z.string().nullish(),
  oportunidade: z.string().nullish(),
  fonte_url: z.string().nullish(),
  dados_raw: z.unknown().nullish(),
  verificado_em: z.string().nullish(),
});
export type Verification = z.infer<typeof verificationSchema>;

const blocoSchema = z.object({
  score: z.coerce.number().nullish(),
  nivel: nivelSchema.nullish(),
  verificacoes: z.array(verificationSchema).default([]),
  // Back-end retorna [{verificacao, oportunidade}] (objects), não strings.
  // Permissivo via z.unknown() — UI só lê .length.
  oportunidades: z.array(z.unknown()).default([]),
});

const imovelDetailSchema = z.object({
  imovel_id: z.union([z.coerce.number(), z.string()]).transform(String),
  denominacao: z.string().nullish(),
  car_codigo: z.string().nullish(),
  municipio: z.string().nullish(),
  score: z.coerce.number().nullish(),
  nivel: nivelSchema.nullish(),
  verificacoes: z.array(verificationSchema).default([]),
  // Back-end retorna [{verificacao, oportunidade}] (objects), não strings.
  // Permissivo via z.unknown() — UI só lê .length.
  oportunidades: z.array(z.unknown()).default([]),
});
export type ImovelDetail = z.infer<typeof imovelDetailSchema>;

export const diagnosticoDetailSchema = z.object({
  cpf: z.string(),
  nome: z.string(),
  municipio: z.string().nullish(),
  score: z.coerce.number().nullish(),
  nivel: nivelSchema.nullish(),
  pessoa: blocoSchema.default({}),
  imoveis: z.array(imovelDetailSchema).default([]),
  total_imoveis: z.coerce.number().default(0),
});
export type DiagnosticoDetail = z.infer<typeof diagnosticoDetailSchema>;

// ONR Sinarma — enriquecimento opcional do detalhe
export const onrItemSchema = z.object({
  id: z.union([z.coerce.number(), z.string()]).transform(String),
  denominacao: z.string().nullish(),
  car_codigo: z.string().nullish(),
  municipio: z.string().nullish(),
  uf: z.string().nullish(),
  onr: z
    .object({
      cod_imovel: z.string().nullish(),
      ind_status: z.string().nullish(),
      des_condic: z.string().nullish(),
      ind_tipo: z.string().nullish(),
      num_area: z.coerce.number().nullish(),
      municipio: z.string().nullish(),
      dat_atuali: z.string().nullish(),
    })
    .nullable(),
  erro: z.string().nullish(),
});
export type OnrItem = z.infer<typeof onrItemSchema>;

export const onrResponseSchema = z.object({
  imoveis: z.array(onrItemSchema).default([]),
  msg: z.string().nullish(),
});

// Consulta avulsa — Python retorna estrutura ad-hoc; aceitamos campos básicos.
export const consultaAvulsaSchema = z.object({
  cpf: z.string(),
  nome: z.string().nullish(),
  score: z.coerce.number().nullish(),
  nivel: nivelSchema.nullish(),
  verificacoes: z.array(verificationSchema).default([]),
  ts: z.string().nullish(),
});
export type ConsultaAvulsa = z.infer<typeof consultaAvulsaSchema>;

// Varredura
export const varreduraIniciadaSchema = z.object({
  status: z.string(),
  total: z.coerce.number().nullish(),
  message: z.string().nullish(),
});

export const varreduraStatusSchema = z.object({
  status: z.string(),
  atual: z.coerce.number().default(0),
  total: z.coerce.number().default(0),
  nome_atual: z.string().nullish(),
  em_andamento: z.boolean().default(false),
  erros: z.coerce.number().default(0),
});
export type VarreduraStatus = z.infer<typeof varreduraStatusSchema>;
